import { writable } from 'svelte/store';
import type {
	RoolChannel,
	RoolSpace,
	RoolSpaceInfo,
	CurrentUser,
	RoolClient,
	ChannelInfo,
} from '@rool-dev/sdk';
import type { UINode, UIEdge, Camera, GridStyle } from '@rool/renderer';
import type { DirectCanvas } from '@rool/renderer';
import type { CanvasEditor } from '@rool/editor';

export const roolClient = writable<RoolClient | null>(null);
export const spaces = writable<RoolSpaceInfo[]>([]);
export const isInitializing = writable<boolean>(true);
export const promptInput = writable<string>('');
export const isPrompting = writable<boolean>(false);
export const nodes = writable<UINode[]>([]);
export const edges = writable<UIEdge[]>([]);
export const channel = writable<RoolChannel | null>(null);
export const spaceAdmin = writable<RoolSpace | null>(null);
/** @deprecated Use `channel` instead. Alias kept for backward compatibility during migration. */
export const space = channel;
export const selectedNodeIds = writable<Set<string>>(new Set());
export const interactionsUpdateTrigger = writable<number>(0);
export const cursorTarget = writable<{
	x: number;
	y: number;
	source?: string;
} | null>(null);
export const viewport = writable<Camera>({ x: 0, y: 0, zoom: 1 });
export const focusNodeRequest = writable<string | null>(null);
export const editNodeRequest = writable<string | null>(null);
export const canvasInstance = writable<DirectCanvas | null>(null);
export const canvasEditor = writable<CanvasEditor | null>(null);
export const activeToolId = writable<string | null>(null);
// Initialize theme from localStorage for instant load, server value is authoritative
const cachedTheme =
	typeof localStorage !== 'undefined'
		? (localStorage.getItem('theme') as 'light' | 'dark' | null)
		: null;
export const theme = writable<'light' | 'dark'>(
	cachedTheme === 'dark' ? 'dark' : 'light',
);
export const gridStyle = writable<GridStyle>('hex');
export const gridSnapping = writable<boolean>(true);
export const showCommandPalette = writable<boolean>(false);
export const currentUser = writable<CurrentUser | null>(null);
export const isChatOpen = writable<boolean>(false);
export const unreadChatCount = writable<number>(0);
export const conversations = writable<ChannelInfo[]>([]);

// Prompt history — shared across MiniChat and ChatPanel
export const promptHistory = (() => {
	const history: string[] = [];
	let cursor = -1; // -1 = not browsing, 0 = most recent, etc.
	let draft = ''; // what the user was typing before pressing up

	return {
		/** Record a sent prompt */
		push(text: string) {
			// Deduplicate consecutive identical entries
			if (history.length === 0 || history[0] !== text) {
				history.unshift(text);
			}
			cursor = -1;
			draft = '';
		},
		/** Move up (older). Returns the prompt text or null if at end. */
		up(currentInput: string): string | null {
			if (history.length === 0) return null;
			if (cursor === -1) {
				draft = currentInput; // save current input as draft
			}
			if (cursor < history.length - 1) {
				cursor++;
				return history[cursor];
			}
			return null; // already at oldest
		},
		/** Move down (newer). Returns the prompt text, or empty string at bottom. */
		down(): string | null {
			if (cursor <= -1) return null;
			cursor--;
			if (cursor === -1) {
				return draft; // restore what user was typing
			}
			return history[cursor];
		},
		/** Reset cursor (e.g. on submit or manual edit) */
		reset() {
			cursor = -1;
			draft = '';
		},
		/** Seed history from existing interactions (e.g. on space load).
		 *  Expects prompts in chronological order (oldest first). */
		seed(prompts: string[]) {
			history.length = 0;
			// Push each prompt using the normal push method (which deduplicates)
			for (const text of prompts) {
				if (history.length === 0 || history[0] !== text) {
					history.unshift(text);
				}
			}
			cursor = -1;
			draft = '';
		},
	};
})();
